import { call, put, takeEvery } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import * as types from '../constants/almataConstants';
import * as actions from '../actions/almataActions';
import * as api from '../../api/almataApi';

// Get Almata Partners Saga
function* getAlmataPartnersSaga(action) {
  try {
    const response = yield call(api.getAlmataPartnersApi, action.payload);
    yield put(actions.getAlmataPartnersSuccess(response));
  } catch (error) {
    yield put(actions.getAlmataPartnersFailure(error));
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch Almata partners';
    toast.error(errorMessage);
  }
}

// Add Almata Partner Saga
function* addAlmataPartnerSaga(action) {
  try {
    const response = yield call(api.addAlmataPartnerApi, action.payload);
    yield put(actions.addAlmataPartnerSuccess(response));
    
    // Refresh the partners list
    if (action.payload.refreshParams) {
      yield put(actions.getAlmataPartners(action.payload.refreshParams));
    }
    
    toast.success(response.message || 'Almata partner added successfully');
  } catch (error) {
    yield put(actions.addAlmataPartnerFailure(error));
    const errorMessage = error.response?.data?.message || error.message || 'Failed to add Almata partner';
    toast.error(errorMessage);
  }
}

// Edit Almata Partner Saga
function* editAlmataPartnerSaga(action) {
  try {
    const response = yield call(api.editAlmataPartnerApi, action.payload);
    yield put(actions.editAlmataPartnerSuccess(response));
    
    // Refresh the partners list
    if (action.payload.refreshParams) {
      yield put(actions.getAlmataPartners(action.payload.refreshParams));
    }
    
    toast.success(response.message || 'Almata partner updated successfully');
  } catch (error) {
    yield put(actions.editAlmataPartnerFailure(error));
    const errorMessage = error.response?.data?.message || error.message || 'Failed to update Almata partner';
    toast.error(errorMessage);
  }
}

// Delete Almata Partner Saga
function* deleteAlmataPartnerSaga(action) {
  try {
    const response = yield call(api.deleteAlmataPartnerApi, action.payload.id);
    yield put(actions.deleteAlmataPartnerSuccess(response));
    
    // Refresh the partners list
    if (action.payload.refreshParams) {
      yield put(actions.getAlmataPartners(action.payload.refreshParams));
    }
    
    toast.success(response.message || 'Almata partner deleted successfully');
  } catch (error) {
    yield put(actions.deleteAlmataPartnerFailure(error));
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete Almata partner';
    toast.error(errorMessage);
  }
}

// Change Almata Status Saga
function* changeAlmataStatusSaga(action) {
  try {
    const { _id, status } = action.payload;
    const response = yield call(api.changeAlmataStatusApi, _id, status);
    yield put(actions.changeAlmataStatusSuccess(response));
    
    // Refresh the partners list
    if (action.payload.refreshParams) {
      yield put(actions.getAlmataPartners(action.payload.refreshParams));
    }
    
    toast.success(response.message || 'Almata partner status updated successfully');
  } catch (error) {
    yield put(actions.changeAlmataStatusFailure(error));
    const errorMessage = error.response?.data?.message || error.message || 'Failed to update Almata partner status';
    toast.error(errorMessage);
  }
}

// Get Scan Types Saga
function* getScanTypesSaga() {
  try {
    const response = yield call(api.getScanTypesApi);
    yield put(actions.getScanTypesSuccess(response.scanTypes || response));
  } catch (error) {
    yield put(actions.getScanTypesFailure(error));
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch scan types';
    toast.error(errorMessage);
  }
}

// Get OEM Partners Saga
function* getOemPartnersSaga(action) {
  try {
    const response = yield call(api.getOemPartnersApi, action.payload);
    yield put(actions.getOemPartnersSuccess(response));
  } catch (error) {
    yield put(actions.getOemPartnersFailure(error));
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch OEM partners';
    toast.error(errorMessage);
  }
}

// Get OEM Partner Users Saga
function* getOemPartnerUsersSaga(action) {
  try {
    const response = yield call(api.getOemPartnerUsersApi, action.payload);
    yield put(actions.getOemPartnerUsersSuccess(response));
  } catch (error) {
    yield put(actions.getOemPartnerUsersFailure(error));
    const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch OEM partner users';
    toast.error(errorMessage);
  }
}

// Reset Almata Password Saga
function* resetAlmataPasswordSaga(action) {
  try {
    const response = yield call(api.resetAlmataPasswordApi, action.payload);
    yield put(actions.resetAlmataPasswordSuccess(response));
    toast.success('Password reset successfully and email sent!');
  } catch (error) {
    yield put(actions.resetAlmataPasswordFailure(error));
    const errorMessage = error.response?.data?.message || error.message || 'Failed to reset password';
    toast.error(errorMessage);
  }
}

// Resend Almata Verification Email Saga
function* resendAlmataVerificationEmailSaga(action) {
  try {
    const response = yield call(api.resendAlmataVerificationEmailApi, action.payload);
    yield put(actions.resendAlmataVerificationEmailSuccess(response));
    toast.success('Verification email sent successfully!');
  } catch (error) {
    yield put(actions.resendAlmataVerificationEmailFailure(error));
    const errorMessage = error.response?.data?.message || error.message || 'Failed to send verification email';
    toast.error(errorMessage);
  }
}

// Watcher Saga
function* almataSaga() {
  yield takeEvery(types.GET_ALMATA_PARTNERS_REQUEST, getAlmataPartnersSaga);
  yield takeEvery(types.ADD_ALMATA_PARTNER_REQUEST, addAlmataPartnerSaga);
  yield takeEvery(types.EDIT_ALMATA_PARTNER_REQUEST, editAlmataPartnerSaga);
  yield takeEvery(types.DELETE_ALMATA_PARTNER_REQUEST, deleteAlmataPartnerSaga);
  yield takeEvery(types.CHANGE_ALMATA_STATUS_REQUEST, changeAlmataStatusSaga);
  yield takeEvery(types.RESET_ALMATA_PASSWORD_REQUEST, resetAlmataPasswordSaga);
  yield takeEvery(types.GET_ALMATA_SCAN_TYPES_REQUEST, getScanTypesSaga);
  yield takeEvery(types.GET_OEM_PARTNERS_REQUEST, getOemPartnersSaga);
  yield takeEvery(types.GET_OEM_PARTNER_USERS_REQUEST, getOemPartnerUsersSaga);
  yield takeEvery(types.RESEND_ALMATA_VERIFICATION_EMAIL_REQUEST, resendAlmataVerificationEmailSaga);
}

export default almataSaga;